import React, { useEffect, useMemo, useState } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Button,
  LinearProgress,
  Chip,
  Avatar,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
} from '@mui/material';
import {
  TrendingUp,
  Campaign,
  People,
  ContentCopy,
  ArrowUpward,
  ArrowDownward,
  MoreVert,
  PlayArrow,
  Schedule,
  CheckCircle,
} from '@mui/icons-material';
import { brandColors } from '../../theme';
import CommandPalette, { CommandItem } from '../../components/CommandPalette/CommandPalette';
import { useNavigate } from 'react-router-dom';

interface StatsCardProps {
  title: string;
  value: string;
  change: string;
  trend: 'up' | 'down';
  icon: React.ReactNode;
  color: string;
}

const StatsCard: React.FC<StatsCardProps> = ({ title, value, change, trend, icon }) => (
  <Card sx={{
    height: '100%',
    border: '1px solid var(--line)',
    backgroundColor: 'var(--card)',
    borderRadius: 'var(--radius)',
    boxShadow: 'var(--shadow-md)'
  }}>
    <CardContent>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, color: 'var(--muted)' }}>
          <Avatar sx={{ bgcolor: 'var(--slate-100)', color: 'var(--ink)', width: 40, height: 40 }}>
            {icon}
          </Avatar>
          <Typography variant="body2" sx={{ fontWeight: 600, color: 'var(--muted)' }}>{title}</Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          {trend === 'up' ? (
            <ArrowUpward sx={{ color: 'var(--success)', fontSize: 18 }} />
          ) : (
            <ArrowDownward sx={{ color: 'var(--danger)', fontSize: 18 }} />
          )}
          <Typography variant="caption" sx={{ fontWeight: 700, color: trend === 'up' ? 'var(--success)' : 'var(--danger)' }}>
            {change}
          </Typography>
        </Box>
      </Box>
      <Typography className="tabular-nums" sx={{ fontWeight: 700, letterSpacing: '-0.01em', color: 'var(--ink)', fontSize: '1.5rem' }}>
        {value}
      </Typography>
      <Box sx={{ mt: 2, height: 6, width: '100%', borderRadius: 999, backgroundColor: 'var(--slate-100)', overflow: 'hidden' }}>
        <Box sx={{ height: '100%', width: '64%', backgroundColor: 'var(--brand)' }} />
      </Box>
    </CardContent>
  </Card>
);

const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const [paletteOpen, setPaletteOpen] = useState(false);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setPaletteOpen(true);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const recentCampaigns = [
    { id: 1, name: 'Sommer Handwerker Kampagne', status: 'active', progress: 75 },
    { id: 2, name: 'Herbst Promotion 2025', status: 'scheduled', progress: 100 },
    { id: 3, name: 'Weihnachts Special', status: 'draft', progress: 30 },
    { id: 4, name: 'Neujahr Angebote', status: 'planning', progress: 15 },
  ];

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'var(--brand)';
      case 'scheduled': return 'var(--warning)';
      case 'draft': return 'var(--slate-600)';
      default: return 'var(--slate-600)';
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'active': return 'Aktiv';
      case 'scheduled': return 'Geplant';
      case 'draft': return 'Entwurf';
      default: return 'Planung';
    }
  };

  const commandItems: CommandItem[] = useMemo(() => ([
    { id: 'new-campaign', label: 'Neue Kampagne', hint: 'Erstellen und planen', icon: <Campaign />, onSelect: () => navigate('/campaigns') },
    { id: 'new-content', label: 'Content erstellen', hint: 'Assets und Posts', icon: <ContentCopy />, onSelect: () => navigate('/content') },
    { id: 'open-analytics', label: 'Analytics anzeigen', hint: 'Leistung prüfen', icon: <TrendingUp />, onSelect: () => navigate('/analytics') },
    { id: 'manage-team', label: 'Team verwalten', hint: 'Rollen & Nutzer', icon: <People />, onSelect: () => navigate('/users') },
  ]), [navigate]);

  return (
    <Box>
      {/* Hero Section (reduced, neutral) */}
      <Box sx={{ mb: 4, p: 3, borderRadius: 'var(--radius)', border: '1px solid var(--line)', backgroundColor: 'var(--card)' }}>
        <Typography variant="h2" sx={{ mb: 1, fontWeight: 600 }}>
          Willkommen bei R2B Platform
        </Typography>
        <Typography variant="body1" sx={{ color: 'var(--muted)', mb: 3 }}>
          Ihre zentrale Plattform für Kampagnen-Management und Content-Erstellung
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Button variant="contained" onClick={() => navigate('/campaigns')}>
            Neue Kampagne starten
          </Button>
          <Button variant="text" sx={{ ml: 2, color: 'var(--ink)' }} onClick={() => navigate('/content')}>
            Content erstellen
          </Button>
        </Box>
      </Box>

      {/* Stats Cards */}
      <Box sx={{ 
        display: 'grid', 
        gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: '1fr 1fr 1fr 1fr' }, 
        gap: 3, 
        mb: 4 
      }}>
        <StatsCard title="Aktive Kampagnen" value="24" change="+12%" trend="up" icon={<Campaign />} color={brandColors.electricTeal} />
        <StatsCard title="Content Elemente" value="156" change="+23%" trend="up" icon={<ContentCopy />} color={brandColors.neonLime} />
        <StatsCard title="Aktive Nutzer" value="89" change="+5%" trend="up" icon={<People />} color={brandColors.midnightSteel} />
        <StatsCard title="Performance Score" value="94%" change="-2%" trend="down" icon={<TrendingUp />} color={brandColors.electricTeal} />
      </Box>

      {/* Recent Campaigns & Quick Actions */}
      <Box sx={{ 
        display: 'grid', 
        gridTemplateColumns: { xs: '1fr', md: '2fr 1fr' }, 
        gap: 3 
      }}>
        <Box>
          <Card>
            <CardContent>
              <Typography variant="h5" sx={{ mb: 3, fontWeight: 600, color: brandColors.midnightSteel }}>
                Aktuelle Kampagnen
              </Typography>
              <List>
                {recentCampaigns.map((campaign) => (
                  <ListItem key={campaign.id} sx={{ mb: 2, border: '1px solid #f0f0f0', borderRadius: 2 }}>
                    <ListItemAvatar>
                      <Avatar sx={{ bgcolor: getStatusColor(campaign.status) }}>
                        {campaign.status === 'active' ? <PlayArrow /> : 
                         campaign.status === 'scheduled' ? <Schedule /> : <CheckCircle />}
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={
                        <Typography variant="subtitle1" sx={{ fontWeight: 600, color: brandColors.midnightSteel }}>
                          {campaign.name}
                        </Typography>
                      }
                      secondary={
                        <Box sx={{ mt: 1 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                            <Chip 
                              label={getStatusText(campaign.status)} 
                              size="small" 
                              sx={{ 
                                bgcolor: `${getStatusColor(campaign.status)}20`,
                                color: getStatusColor(campaign.status),
                                fontWeight: 600
                              }}
                            />
                            <Typography variant="body2" color="text.secondary">
                              {campaign.progress}%
                            </Typography>
                          </Box>
                          <LinearProgress 
                            variant="determinate" 
                            value={campaign.progress} 
                            sx={{ 
                              height: 6, 
                              borderRadius: 3,
                              backgroundColor: 'var(--slate-100)',
                              '& .MuiLinearProgress-bar': {
                                backgroundColor: 'var(--brand)'
                              }
                            }}
                          />
                        </Box>
                      }
                    />
                    <ListItemSecondaryAction>
                      <IconButton edge="end">
                        <MoreVert />
                      </IconButton>
                    </ListItemSecondaryAction>
                  </ListItem>
                ))}
              </List>
            </CardContent>
          </Card>
        </Box>
        
        <Box>
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: brandColors.midnightSteel }}>
                Schnellaktionen
              </Typography>
              <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
                {[
                  { icon: <Campaign />, title: 'Neue Kampagne', hint: 'Erstellen', onClick: () => navigate('/campaigns') },
                  { icon: <ContentCopy />, title: 'Content', hint: 'Neu', onClick: () => navigate('/content') },
                  { icon: <TrendingUp />, title: 'Analytics', hint: 'Berichte', onClick: () => navigate('/analytics') },
                  { icon: <People />, title: 'Team', hint: 'Verwalten', onClick: () => navigate('/users') },
                ].map((tile, idx) => (
                  <Box key={idx} onClick={tile.onClick} sx={{
                    p: 2,
                    border: '1px solid var(--line)',
                    borderRadius: 'var(--radius)',
                    width: 'calc(50% - 6px)',
                    cursor: 'pointer',
                    backgroundColor: 'var(--card)',
                    transition: 'transform 180ms cubic-bezier(0.2,0,0,1), box-shadow 180ms',
                    '&:hover': { transform: 'translateY(-2px)', boxShadow: 'var(--shadow-sm)' }
                  }}>
                    <Box sx={{ color: 'var(--muted)' }}>{tile.icon}</Box>
                    <Typography variant="subtitle2" sx={{ mt: 1, fontWeight: 700 }}>{tile.title}</Typography>
                    <Typography variant="caption" sx={{ color: 'var(--muted)' }}>{tile.hint}</Typography>
                  </Box>
                ))}
              </Box>
              <Box sx={{ mt: 2 }}>
                <Button variant="outlined" fullWidth onClick={() => setPaletteOpen(true)}>
                  Command Palette öffnen (⌘K / Ctrl+K)
                </Button>
              </Box>
            </CardContent>
          </Card>

          <Card>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: brandColors.midnightSteel }}>
                Heute geplant
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Box sx={{ p: 2, bgcolor: 'var(--slate-50)', borderRadius: 2, border: '1px solid var(--line)' }}>
                  <Typography variant="body2" sx={{ fontWeight: 600, color: brandColors.midnightSteel }}>
                    10:00 - Content Review
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Sommer Kampagne Materialien
                  </Typography>
                </Box>
                <Box sx={{ p: 2, bgcolor: 'var(--slate-50)', borderRadius: 2, border: '1px solid var(--line)' }}>
                  <Typography variant="body2" sx={{ fontWeight: 600, color: brandColors.midnightSteel }}>
                    14:30 - Kampagnen-Meeting
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Q4 Planung Besprechung
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Box>
      </Box>

      <CommandPalette open={paletteOpen} onClose={() => setPaletteOpen(false)} items={commandItems} />
    </Box>
  );
};

export default Dashboard;
